/**
 * ui/services/complianceService.ts
 *
 * v6.5 patch goals:
 * - 修正 export 缺失（refreshSeedCards）
 * - 修正 Firestore schema 與 UI 的 IComplianceCard 對齊
 * - Seed CSV -> 轉成專案管控卡片（寫入 compliance_cards）
 * - 提供最小相容：舊 signature listComplianceCards(type, includeHistory)
 */

import {
  firebase,
  db,
  functions,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  orderBy,
  where,
  limit,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from "../../firebaseClient";

import type {
  ComplianceCard as IComplianceCard,
  ComplianceCardType as CardType,
  ComplianceCardStatus as CardStatus,
  ComplianceRiskLevel as RiskLevel,
  RegIntelStatus,
  ConvertEligibility,
  ComplianceProjectTask,
  CrawlerRunLog,
} from "../types/compliance";

import type { AttachmentMeta } from "../types/attachments";
import type { ApprovalModule } from "../types/approval";
import { createOrResetApprovalFlow } from "./approvalFlowService";

// ui/constants/departments.ts defines the actual codes; we keep it flexible here.
type DeptCode = string;

// ----------------------------
// Constants & helpers
// ----------------------------

const CARDS_COLLECTION = "compliance_cards";
const RUNS_COLLECTION = "crawlerRuns";

const HISTORY_STATUSES: CardStatus[] = ["ACK_ARCHIVED", "COMPLETED"];

// Helper for UI bucketing (e.g., "歷史彙編" tab).
export function isComplianceHistoryStatus(status?: string | null): boolean {
  const s = (status || "").toUpperCase();
  return HISTORY_STATUSES.includes(s as CardStatus);
}

function normalizeType(raw: any): CardType {
  const t = String(raw ?? "").toLowerCase();
  if (t === "intel") return "intel";
  if (t === "project") return "project";
  // legacy field
  const k = String(raw?.kind ?? "").toLowerCase();
  if (k === "seed" || k === "project") return "project";
  return "intel";
}

function normalizeStatus(raw: any): CardStatus {
  const s = String(raw ?? "").toUpperCase();

  // Align with ui/types/compliance.ts (ComplianceCardStatus)
  const map: Record<string, CardStatus> = {
    DRAFT: "DRAFT",
    IN_REVIEW: "IN_REVIEW",
    APPROVED: "APPROVED",
    REJECTED: "REJECTED",
    RETURNED: "RETURNED",
    NOT_STARTED: "NOT_STARTED",
    COMPLETED: "COMPLETED",
    ACK_ARCHIVED: "ACK_ARCHIVED",
    MOVED_TO_PROJECT: "MOVED_TO_PROJECT",
    // legacy
    ARCHIVED: "ACK_ARCHIVED",
    PENDING: "IN_REVIEW",
    IN_PROGRESS: "IN_REVIEW",
  };

  return map[s] ?? "NOT_STARTED";
}

function normalizeRiskLevel(raw: any): RiskLevel {
  const s = String(raw ?? "").toUpperCase();
  if (s === "HIGH") return "HIGH";
  if (s === "MEDIUM") return "MEDIUM";
  if (s === "LOW") return "LOW";
  return "MEDIUM";
}


// ----------------------------
// Regulatory Intel (auto inference for legacy cards)
// ----------------------------

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function inferDocTypeLocal(title: string, summary: string, hinted?: any): string {
  const h = safeString(hinted, "").toUpperCase();
  if (["PREVIEW","ANNOUNCEMENT","ORDER","INTERPRETATION","REGULATION_TEXT","NEWS","OTHER"].includes(h)) return h;

  const text = `${title}\n${summary}`;
  const has = (re: RegExp) => re.test(text);
  if (has(/預告|草案|修正草案|意見徵詢|徵求意見|公開徵求意見|諮詢|公聽會|座談會/)) return "PREVIEW";
  if (has(/函釋|釋示|問答|Q\s*&\s*A|Q\s*\/?\s*A|FAQ/)) return "INTERPRETATION";
  if (has(/解釋令/)) return "ORDER";
  if (has(/\b令\b/)) return "ORDER";
  if (has(/法規|條文|修正條文|對照表/)) return "REGULATION_TEXT";
  if (has(/新聞稿|活動|說明會|宣導/)) return "NEWS";
  if (has(/公告|公布|發布|核定/)) return "ANNOUNCEMENT";
  return "OTHER";
}


function normalizeDateISO(input: string): string {
  const s = safeString(input, "").trim();
  if (!s) return "";
  // YYYY-MM-DD or YYYY/MM/DD
  let m = s.match(/^(\d{4})[\/-](\d{1,2})[\/-](\d{1,2})/);
  if (m) {
    const y = m[1];
    const mo = m[2].padStart(2, "0");
    const d = m[3].padStart(2, "0");
    return `${y}-${mo}-${d}`;
  }
  // ROC: 114-11-19 / 114/11/19 / 民國114年11月19日
  m = s.match(/^(?:民國)?\s*(\d{2,3})\s*(?:年|[\/-])\s*(\d{1,2})\s*(?:月|[\/-])\s*(\d{1,2})/);
  if (m) {
    const y = String(Number(m[1]) + 1911);
    const mo = m[2].padStart(2, "0");
    const d = m[3].padStart(2, "0");
    return `${y}-${mo}-${d}`;
  }
  return "";
}

function extractObligationCandidatesLocal(title: string, summary: string): string[] {
  const text = `${title}\n${summary}`.replace(/\s+/g, " ").trim();
  const parts = text.split(/[。！？；\n]/);
  const out: string[] = [];
  const hit = (s: string) => /(必須|應|不得|須|應於|應依|應建立|應設|應保存|應留存|不得為)/.test(s);
  for (const p0 of parts) {
    const p = p0.trim();
    if (!p) continue;
    if (hit(p)) {
      out.push(p.length > 200 ? p.slice(0, 200) : p);
      if (out.length >= 3) break;
    }
  }
  if (out.length === 0 && hit(title)) out.push(title.length > 120 ? title.slice(0, 120) : title);
  return out;
}

function extractEvidenceSnippetsLocal(title: string, summary: string, obligations: string[]): string[] {
  const out: string[] = [];
  for (const o of obligations || []) {
    if (!o) continue;
    out.push(o.length > 200 ? o.slice(0, 200) : o);
    if (out.length >= 3) return out;
  }
  const text = `${title}\n${summary}`.replace(/\s+/g, " ").trim();
  if (text) out.push(text.length > 200 ? text.slice(0, 200) : text);
  return out.slice(0, 3);
}

function inferStatusAutoLocal(args: {
  docType: string;
  title: string;
  summary: string;
  publishDateISO?: string;
  effectiveDateISO?: string | null;
}): RegIntelStatus {
  const { docType, title, summary, publishDateISO, effectiveDateISO } = args;
  const text = `${title}\n${summary}`;

  if (/廢止|停止適用|停止受理|撤銷|取代/.test(text)) return "S4_SUPERSEDED";
  if (/不適用|僅適用/.test(text)) return "S5_NOT_APPLICABLE";

  if (docType === "PREVIEW" || /預告|草案|修正草案|意見徵詢|徵求意見|公開徵求意見/.test(text)) {
    return "S1_DRAFT_FOR_COMMENT";
  }

const effRaw = safeString(effectiveDateISO, "");
const pubRaw = safeString(publishDateISO, "");
const today = todayIso();

const eff = normalizeDateISO(effRaw);
const pub = normalizeDateISO(pubRaw);

if (eff) {
  return eff > today ? "S2_ANNOUNCED_NOT_EFFECTIVE" : "S3_EFFECTIVE_OBLIGATION";
}

  if (/即日生效|即日起施行|自發布日施行|自公告日施行|自發布日起施行|自公告日起施行/.test(text)) {
    return "S3_EFFECTIVE_OBLIGATION";
  }

// For official announcements/orders without explicit effective date,
// default to "effective on publish date" unless evidence suggests future effect.
if (["ANNOUNCEMENT", "ORDER", "REGULATION_TEXT"].includes(docType)) {
  if (pub) return pub > today ? "S2_ANNOUNCED_NOT_EFFECTIVE" : "S3_EFFECTIVE_OBLIGATION";
  return "S2_ANNOUNCED_NOT_EFFECTIVE";
}

if (pub) return "S2_ANNOUNCED_NOT_EFFECTIVE";

  return "S0_UNKNOWN";
}

function inferRiskAutoLocal(args: {
  statusAuto: RegIntelStatus;
  docType: string;
  title: string;
  summary: string;
  obligationCandidates: any;
}): RiskLevel {
  const { statusAuto, docType, title, summary } = args;
  const obligations = Array.isArray(args.obligationCandidates) ? args.obligationCandidates.filter(Boolean) : [];
  const text = `${title}\n${summary}`;
  const hasObligation = obligations.length > 0 || /必須|應|不得|須|應於/.test(text);

  if (statusAuto === "S4_SUPERSEDED" || statusAuto === "S5_NOT_APPLICABLE") return "LOW";
  if (docType === "NEWS" && !hasObligation) return "LOW";

  if (statusAuto === "S3_EFFECTIVE_OBLIGATION") {
    return hasObligation ? "HIGH" : "MEDIUM";
  }

  if (statusAuto === "S1_DRAFT_FOR_COMMENT" || statusAuto === "S2_ANNOUNCED_NOT_EFFECTIVE") return "MEDIUM";

  return hasObligation ? "MEDIUM" : "LOW";
}

function inferConvertEligibilityLocal(args: {
  finalStatus: RegIntelStatus;
  effectiveDateISO?: string | null;
  obligationCandidates: any;
}): ConvertEligibility {
  const { finalStatus, effectiveDateISO } = args;
  const obligations = Array.isArray(args.obligationCandidates) ? args.obligationCandidates.filter(Boolean) : [];

  if (finalStatus === "S3_EFFECTIVE_OBLIGATION") {
    if (obligations.length === 0) return { ok: false, reasonCode: "NO_OBLIGATION_CANDIDATES", reason: "未解析到明確義務句（請人工補充後再轉）" };
    return { ok: true, mode: "CONVERT" };
  }

  if (finalStatus === "S2_ANNOUNCED_NOT_EFFECTIVE") {
    const eff = safeString(effectiveDateISO, "");
    if (eff) {
      const d = new Date(eff + "T00:00:00Z");
      const now = new Date();
      const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
      const days = Math.ceil((d.getTime() - today.getTime()) / 86400000);
      if (days >= 0 && days <= 30) return { ok: true, mode: "PREPARE" };
    }
    return { ok: false, reasonCode: "NOT_EFFECTIVE_YET", reason: "已公告但未生效（或生效日未知）" };
  }

  if (finalStatus === "S1_DRAFT_FOR_COMMENT") return { ok: false, reasonCode: "DRAFT_FOR_COMMENT", reason: "草案/徵詢階段，不開放轉專案" };
  if (finalStatus === "S4_SUPERSEDED") return { ok: false, reasonCode: "SUPERSEDED", reason: "已失效/被取代" };
  if (finalStatus === "S5_NOT_APPLICABLE") return { ok: false, reasonCode: "NOT_APPLICABLE", reason: "不適用" };

  return { ok: false, reasonCode: "UNKNOWN", reason: "狀態未知/資料不足" };
}

function safeString(v: any, fallback = ""): string {
  return typeof v === "string" ? v : v == null ? fallback : String(v);
}

function clampInt(n: any, fallback: number): number {
  const x = Number(n);
  if (!Number.isFinite(x)) return fallback;
  return Math.round(x);
}

function mapOwnerDept(raw: string): DeptCode {
  const v = safeString(raw).trim();
  const u = v.toUpperCase();

  // Accept both dept codes (GM/FA/AP/IT/LG/SL1/SL2/LI/PT/QC/MK/SYS) and Chinese hints.
  const direct = ["GM", "FA", "AP", "IT", "LG", "SL1", "SL2", "LI", "PT", "QC", "MK", "SYS"] as const;
  if ((direct as readonly string[]).includes(u)) return u as DeptCode;

  if (/[法務]/.test(v)) return "LG";
  if (/(行銷|市場|廣告)/.test(v)) return "MK";
  if (/(資訊|IT|資安|系統)/i.test(v)) return "IT";
  if (/(財務|會計|稅務|發票)/.test(v)) return "FA";
  if (/(人資|行政|HR)/i.test(v)) return "AP";
  if (/(總經理)/.test(v)) return "GM";
  if (/(業務一)/.test(v)) return "SL1";
  if (/(業務二)/.test(v)) return "SL2";
  if (/(物流|倉儲|出貨|配送)/.test(v)) return "LI";
  if (/(採購)/.test(v)) return "PT";
  if (/(品保|品管)/.test(v)) return "QC";
  if (/(系統管理|管理者)/.test(v)) return "SYS";

  return "LG";
}

// ----------------------------
// CSV parsing (minimal, robust enough)
// ----------------------------

function parseCsv(text: string): Array<Record<string, string>> {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;

  const pushField = () => {
    row.push(field);
    field = "";
  };
  const pushRow = () => {
    // ignore completely empty row
    const anyVal = row.some((c) => c.trim() !== "");
    if (anyVal) rows.push(row);
    row = [];
  };

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        const next = text[i + 1];
        if (next === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ",") {
        pushField();
      } else if (ch === "\n") {
        pushField();
        pushRow();
      } else if (ch === "\r") {
        // ignore (handle \r\n)
      } else {
        field += ch;
      }
    }
  }
  // last field/row
  pushField();
  pushRow();

  if (rows.length === 0) return [];

  const headers = rows[0].map((h) => h.trim());
  return rows.slice(1).map((r) => {
    const obj: Record<string, string> = {};
    headers.forEach((h, idx) => {
      obj[h] = (r[idx] ?? "").trim();
    });
    return obj;
  });
}

function safeSnapExists(snap: any): boolean {
  if (!snap) return false;
  const v = (snap as any).exists;
  try {
    return typeof v === "function" ? v.call(snap) : Boolean(v);
  } catch {
    // fallback
    return Boolean(v);
  }
}

function filenameFromUrl(url: string, fallback = "attachment.pdf"): string {
  try {
    const u = new URL(url);
    const last = u.pathname.split("/").filter(Boolean).pop();
    if (!last) return fallback;
    // If it doesn't look like a file, still return something stable
    return decodeURIComponent(last) || fallback;
  } catch {
    const last = url.split("/").filter(Boolean).pop();
    return last || fallback;
  }
}

function toAttachmentMetaArray(links: any): AttachmentMeta[] {
  const arr = Array.isArray(links) ? links : [];
  const items = arr
    .map((x) => {
      const url = safeString(typeof x === "string" ? x : (x as any)?.url);
      if (!url) return null;
      const name = safeString(typeof x === "string" ? "" : (x as any)?.name);
      return { url, name: name || filenameFromUrl(url) };
    })
    .filter(Boolean) as Array<{ url: string; name: string }>;

  return items.map((it) => ({ name: it.name, url: it.url }));
}

async function sha256Hex(input: string): Promise<string> {
  // prefer WebCrypto
  const g: any = globalThis as any;
  const subtle = g?.crypto?.subtle;
  if (subtle?.digest) {
    const enc = new TextEncoder();
    const buf = await subtle.digest("SHA-256", enc.encode(input));
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  }
  // fallback (non-crypto hash)
  let h = 5381;
  for (let i = 0; i < input.length; i++) h = (h * 33) ^ input.charCodeAt(i);
  return (h >>> 0).toString(16).padStart(8, "0");
}

// ----------------------------
// Normalization: Firestore -> IComplianceCard
// ----------------------------

function normalizeDocToCard(id: string, raw: any): IComplianceCard {
  const type = normalizeType(raw?.type ?? raw);
  const status = normalizeStatus(raw?.status);

  const agencyName = safeString(raw?.agencyName ?? raw?.authorityName ?? raw?.sourceName ?? raw?.agency ?? "");
  const agencyId = safeString(raw?.agencyId ?? raw?.authorityId ?? raw?.sourceId ?? raw?.agencyId ?? "");

  // sectionA
  const dateText = safeString(
    raw?.sectionA?.dateText ??
      raw?.dateText ??
      raw?.sectionA?.announcementDate ??
      raw?.announcementDate ??
      raw?.publishedAt ??
      ""
  );
  const date = safeString(raw?.sectionA?.date ?? raw?.date ?? dateText);

  // attachmentLinks can come from either attachmentLinks (string[]) or attachments (AttachmentMeta[])
  const rawLinks =
    raw?.sectionA?.attachmentLinks ??
    raw?.attachmentLinks ??
    (Array.isArray(raw?.sectionA?.attachments) ? raw.sectionA.attachments.map((a: any) => a?.url) : []);

  const attachmentLinks = Array.isArray(rawLinks)
    ? rawLinks.map((x: any) => safeString(typeof x === "string" ? x : x?.url)).filter(Boolean)
    : [];

  const sectionA = {
    title: safeString(raw?.sectionA?.title ?? raw?.title ?? ""),
    subject: safeString(raw?.sectionA?.subject ?? raw?.subject ?? raw?.title ?? ""),
    date: date ? date : null,
    dateText,
    summary: safeString(raw?.sectionA?.summary ?? raw?.summary ?? ""),
    sourceUrl: safeString(raw?.sectionA?.sourceUrl ?? raw?.sourceUrl ?? ""),
    announcementUrl: safeString(raw?.sectionA?.announcementUrl ?? raw?.announcementUrl ?? ""),
    attachmentLinks,
    legalBasis: Array.isArray(raw?.sectionA?.legalBasis ?? raw?.legalBasis)
      ? (raw?.sectionA?.legalBasis ?? raw?.legalBasis).map((x: any) => safeString(x)).filter(Boolean)
      : [],
    tags: raw?.sectionA?.tags ?? raw?.tags ?? [],
  };

  const sectionB = {
    ownerDept: mapOwnerDept(safeString(raw?.sectionB?.ownerDept ?? raw?.ownerDept ?? "LG")),
    ownerDeptSuggestionText: safeString(raw?.sectionB?.ownerDeptSuggestionText ?? raw?.ownerDeptSuggestionText ?? ""),
    content: safeString(raw?.sectionB?.content ?? raw?.content ?? ""),
    attachments: Array.isArray(raw?.sectionB?.attachments)
      ? raw.sectionB.attachments
      : [],
    topicTags: Array.isArray(raw?.sectionB?.topicTags)
      ? raw.sectionB.topicTags
      : [],
  };

  const createdAt = raw?.createdAt ?? null;
  const updatedAt = raw?.updatedAt ?? null;
  const archivedAt = raw?.archivedAt ?? null;

  const riskScore = clampInt(raw?.riskScore, 50);
  let riskLevel: RiskLevel = normalizeRiskLevel(raw?.riskLevel);

  // ---- Auto inference backfill (UI-only) ----
  if (type === "intel") {
    const titleText = sectionA.title;
    const summaryText = sectionA.summary;
    const docType = safeString((sectionA as any).docType, "") || inferDocTypeLocal(titleText, summaryText);
    (sectionA as any).docType = docType as any;

    const pub = safeString((sectionA as any).publishDate, "") || safeString(dateText, "") || (date ? String(date) : "");
    (sectionA as any).publishDate = pub || undefined;

    const statusAuto: RegIntelStatus = (safeString((sectionA as any).statusAuto, "") as any) || inferStatusAutoLocal({
      docType,
      title: titleText,
      summary: summaryText,
      publishDateISO: pub,
      effectiveDateISO: (sectionA as any).effectiveDate,
    });
    (sectionA as any).statusAuto = statusAuto as any;

    const riskAuto: RiskLevel = (safeString((sectionA as any).riskLevelAuto, "") as any) || inferRiskAutoLocal({
      statusAuto,
      docType,
      title: titleText,
      summary: summaryText,
      obligationCandidates: (sectionA as any).obligationCandidates,
    });
    (sectionA as any).riskLevelAuto = riskAuto as any;

    const finalStatus: RegIntelStatus = (safeString((sectionA as any).statusManual, "") as any) || statusAuto;
    if (!(sectionA as any).convertEligibility) {
      (sectionA as any).convertEligibility = inferConvertEligibilityLocal({
        finalStatus,
        effectiveDateISO: (sectionA as any).effectiveDate,
        obligationCandidates: (sectionA as any).obligationCandidates,
      });
    }

    const manualRisk = safeString((sectionA as any).riskLevelManual, "").toUpperCase();
    const finalRisk = (manualRisk as any) || riskAuto || riskLevel;
    riskLevel = normalizeRiskLevel(finalRisk);
  }

  return {
    id,
    type,
    status,
    agencyName,
    agencyId,
    ownerDept: sectionB.ownerDept,
    riskScore,
    riskLevel,
    // minimal sections
    sectionA: {
      ...sectionA,
      attachments: Array.isArray(raw?.sectionA?.attachments)
        ? toAttachmentMetaArray(
            raw.sectionA.attachments.map((a: any) => ({
              name: safeString(a?.name) || filenameFromUrl(safeString(a?.url)),
              url: safeString(a?.url),
            }))
          )
        : toAttachmentMetaArray(attachmentLinks),
      fetchedAt: raw?.sectionA?.fetchedAt ?? null,
    },
    sectionB,
    coAssistSections: Array.isArray(raw?.coAssistSections) ? raw.coAssistSections : [],
    createdAt,
    updatedAt,
    archivedAt,
  };
}

// ----------------------------
// Public API
// ----------------------------

export async function getLatestCrawlerRun(): Promise<CrawlerRunLog | null> {
  const q = query(
    collection(db, RUNS_COLLECTION),
    orderBy("startedAt", "desc"),
    limit(1)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0].data() as any;

  // The UI expects these fields (ui/types/compliance.ts). We keep fallbacks for older run logs.
  return {
    id: snap.docs[0].id,
    startedAt: d.startedAt ?? null,
    finishedAt: d.finishedAt ?? d.endedAt ?? null,
    status: d.status ?? "SUCCESS",
    agenciesCount: d.agenciesCount ?? 0,
    fetchedCount: d.fetchedCount ?? d.processed ?? 0,
    createdCardsCount: d.createdCardsCount ?? d.createdCount ?? 0,
    dedupSkippedCount: d.dedupSkippedCount ?? 0,
    errorMessage: d.errorMessage,
  };
}

// --- listComplianceCards ---
// Overload signatures
export async function listComplianceCards(type?: string, includeHistory = false, includeHidden = false): Promise<IComplianceCard[]> {
  // 🔒 Index-less strategy:
  // Firestore will require a composite index when you combine where(type==...) + orderBy(updatedAt).
  // To keep the demo frictionless (no manual index creation), we:
  // 1) query by type WITHOUT orderBy
  // 2) sort by updatedAt/createdAt on the client
  const lim = type === "project" ? 300 : 200;

  const colRef = collection(db, CARDS_COLLECTION);
  const qRef = type
    ? query(colRef, where("type", "==", type), limit(lim))
    : query(colRef, limit(lim));

  const snap = await getDocs(qRef);

  let list = snap.docs.map((d) => normalizeComplianceCardForUI(d.id, d.data()));

  // Default: hide deduped losers (crawler may set dedupeHidden=true)
  if (!includeHidden) {
    list = list.filter((c) => !(c as any)?.dedupeHidden);
  }

  // History filter (client-side)
  if (!includeHistory) {
    list = list.filter((c) => !isComplianceHistoryStatus(c.status));
  }

  // Client-side sort by updatedAt desc (fallback to createdAt)
  const toMillis = (ts: any): number => {
    if (!ts) return 0;
    if (typeof ts === "number") return ts;
    if (typeof ts === "string") {
      const n = Date.parse(ts);
      return Number.isFinite(n) ? n : 0;
    }
    if (typeof ts?.toMillis === "function") return ts.toMillis();
    if (typeof ts?.seconds === "number") {
      const ns = typeof ts?.nanoseconds === "number" ? ts.nanoseconds : 0;
      return ts.seconds * 1000 + Math.floor(ns / 1e6);
    }
    return 0;
  };

  list.sort((a, b) => {
    const am = toMillis((a as any).updatedAt) || toMillis((a as any).createdAt);
    const bm = toMillis((b as any).updatedAt) || toMillis((b as any).createdAt);
    return bm - am;
  });

  return list;
}

// --- Create / Update / Delete ---

export async function createComplianceCard(input: any): Promise<string> {
  // compat firestore: generate doc id via collection(...).doc()
  const ref = collection(db, CARDS_COLLECTION).doc();
  const now = serverTimestamp();

  const title = safeString(input?.title ?? input?.sectionA?.title, "新專案 (請填寫)");
  const summary = safeString(input?.summary ?? input?.sectionA?.summary, "");

  const ownerDept: DeptCode = safeString(input?.ownerDept ?? input?.sectionB?.ownerDept, "LG");
  const status: CardStatus = normalizeStatus(input?.status ?? "DRAFT");
  const riskLevel: RiskLevel = normalizeRiskLevel(input?.riskLevel ?? "MEDIUM");
  const riskScore = clampInt(input?.riskScore, 50);

  const sectionAIn = input?.sectionA ?? {};
  const sectionBIn = input?.sectionB ?? {};

  const attachmentLinks = Array.isArray(sectionAIn?.attachmentLinks)
    ? sectionAIn.attachmentLinks
    : splitLinks(sectionAIn?.attachmentLinks ?? "");

  const card: Omit<IComplianceCard, "id"> & Record<string, any> = {
    type: normalizeType(input?.type ?? "project"),
    status,
    agencyName: safeString(input?.agencyName ?? input?.agencyId, ""),
    agencyId: safeString(input?.agencyId, ""),
    ownerDept,
    riskScore,
    riskLevel,
    source: safeString(input?.source, "manual"),
    seedKey: safeString(input?.seedKey, ""),
    sectionA: {
      title,
      date: safeString(sectionAIn?.date ?? sectionAIn?.dateText ?? "") || null,
      announcementDate: sectionAIn?.announcementDate ?? null,
      dateText: safeString(sectionAIn?.dateText, ""),
      subject: safeString(sectionAIn?.subject, title),
      summary,
      sourceUrl: safeString(sectionAIn?.sourceUrl, ""),
      announcementUrl: safeString(sectionAIn?.announcementUrl, ""),
      attachmentLinks,
      legalBasis: Array.isArray(sectionAIn?.legalBasis) ? sectionAIn.legalBasis : [],
      attachments: Array.isArray(sectionAIn?.attachments)
        ? toAttachmentMetaArray(sectionAIn.attachments)
        : toAttachmentMetaArray(attachmentLinks),
      fetchedAt: sectionAIn?.fetchedAt ?? null,
    },
    sectionB: {
      ownerDept: safeString(sectionBIn?.ownerDept, ownerDept),
      ownerDeptSuggestionText: safeString(sectionBIn?.ownerDeptSuggestionText, ""),
      content: safeString(sectionBIn?.content, ""),
      attachments: Array.isArray(sectionBIn?.attachments) ? sectionBIn.attachments : [],
      topicTags: Array.isArray(sectionBIn?.topicTags) ? sectionBIn.topicTags : [],
    },
    coAssistSections: Array.isArray(input?.coAssistSections) ? input.coAssistSections : [],
    approvalFlowId: safeString(input?.approvalFlowId, ""),
    approvalHistory: Array.isArray(input?.approvalHistory) ? input.approvalHistory : [],
    rejectionLog: Array.isArray(input?.rejectionLog) ? input.rejectionLog : [],
    regIntelAuditLog: Array.isArray(input?.regIntelAuditLog) ? input.regIntelAuditLog : [],
    dedupeHidden: Boolean(input?.dedupeHidden),
    duplicateOf: safeString(input?.duplicateOf, "") || undefined,
    projectTasks: Array.isArray(input?.projectTasks) ? input.projectTasks : [],
    dueDate: safeString(input?.dueDate, "") || undefined,
    createdAt: null,
    updatedAt: null,
    archivedAt: null,
    createdBy: safeString(input?.createdBy, "unknown"),
  };

  await setDoc(ref, { ...card, createdAt: now, updatedAt: now });
  return ref.id;
}

export async function updateComplianceCard(
  id: string,
  patch: Partial<IComplianceCard>
): Promise<void> {
  const ref = doc(db, CARDS_COLLECTION, id);
  await setDoc(ref, { ...patch, updatedAt: serverTimestamp() }, { merge: true });
}


// ----------------------------
// Approval / status transitions (required by ui/components/compliance/ComplianceCard.tsx)
// ----------------------------

function getUserUid(user: any): string {
  return String(user?.id ?? user?.uid ?? user?.email ?? "unknown");
}

function getUserName(user: any): string {
  return String(user?.displayName ?? user?.name ?? user?.email ?? "unknown");
}


// ----------------------------
// Regulatory Intel helpers (Status/Risk/Eligibility)
// ----------------------------

function parseISODateOnly(iso?: string | null): Date | null {
  const s = safeString(iso, "").trim();
  if (!s) return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) {
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null;
    return new Date(Date.UTC(y, mo - 1, d));
  }
  const t = Date.parse(s);
  if (!Number.isFinite(t)) return null;
  const dt = new Date(t);
  return new Date(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate()));
}

function toISODateOnly(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function addDaysISO(baseISO: string, days: number): string {
  const base = parseISODateOnly(baseISO);
  const now = new Date();
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const d = base || today;
  const out = new Date(d.getTime());
  out.setUTCDate(out.getUTCDate() + days);
  return toISODateOnly(out);
}

function daysUntilISO(targetISO: string): number | null {
  const t = parseISODateOnly(targetISO);
  if (!t) return null;
  const now = new Date();
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const diff = t.getTime() - today.getTime();
  return Math.ceil(diff / 86400000);
}

function computeFinalIntelStatus(card: any): RegIntelStatus {
  const s = safeString(card?.sectionA?.statusManual || card?.sectionA?.statusAuto || "").trim();
  const ok = [
    "S0_UNKNOWN",
    "S1_DRAFT_FOR_COMMENT",
    "S2_ANNOUNCED_NOT_EFFECTIVE",
    "S3_EFFECTIVE_OBLIGATION",
    "S4_SUPERSEDED",
    "S5_NOT_APPLICABLE",
  ].includes(s);
  return (ok ? s : "S0_UNKNOWN") as RegIntelStatus;
}

function computeFinalIntelRisk(card: any): RiskLevel {
  const r = safeString(card?.sectionA?.riskLevelManual || card?.sectionA?.riskLevelAuto || card?.riskLevel || "")
    .toUpperCase()
    .trim();
  if (r === "HIGH" || r === "MEDIUM" || r === "LOW") return r as RiskLevel;
  return "MEDIUM";
}

function computeEligibility(status: RegIntelStatus, effectiveDate?: string | null, obligationCandidates?: any): ConvertEligibility {
  const obligations = Array.isArray(obligationCandidates) ? obligationCandidates.filter(Boolean) : [];

  if (status === "S3_EFFECTIVE_OBLIGATION") {
    if (obligations.length === 0) {
      return {
        ok: false,
        reasonCode: "NO_OBLIGATION_CANDIDATES",
        reason: "未解析到明確義務句，需人工補充後再轉專案",
      };
    }
    return { ok: true, mode: "CONVERT" };
  }

  if (status === "S2_ANNOUNCED_NOT_EFFECTIVE") {
    const days = effectiveDate ? daysUntilISO(effectiveDate) : null;
    if (days != null && days >= 0 && days <= 30) {
      return { ok: true, mode: "PREPARE" };
    }
    return {
      ok: false,
      reasonCode: "NOT_EFFECTIVE_YET",
      reason: "已公告但未到生效日（或生效日未知），不開放一鍵轉專案",
    };
  }

  if (status === "S1_DRAFT_FOR_COMMENT") {
    return {
      ok: false,
      reasonCode: "DRAFT_FOR_COMMENT",
      reason: "目前為草案/徵詢意見階段，建議先觀察或建立追蹤",
    };
  }

  if (status === "S4_SUPERSEDED") {
    return { ok: false, reasonCode: "SUPERSEDED", reason: "已被更新/廢止/停止適用" };
  }

  if (status === "S5_NOT_APPLICABLE") {
    return { ok: false, reasonCode: "NOT_APPLICABLE", reason: "判定不適用" };
  }

  return { ok: false, reasonCode: "UNKNOWN", reason: "資料不足或狀態未知" };
}

function buildObligationSummaryText(card: any): string {
  const cands: string[] = Array.isArray(card?.sectionA?.obligationCandidates)
    ? card.sectionA.obligationCandidates
    : [];
  const evs: string[] = Array.isArray(card?.sectionA?.evidenceSnippets)
    ? card.sectionA.evidenceSnippets
    : [];

  const pick = (arr: any[], n: number) =>
    arr
      .map((x) => safeString(x).trim())
      .filter(Boolean)
      .slice(0, n);

  const lines: string[] = [];

  for (const s of pick(cands, 4)) lines.push(`• ${s}`);
  if (lines.length === 0) for (const s of pick(evs, 2)) lines.push(`• ${s}`);

  if (lines.length === 0) {
    const summary = safeString(card?.sectionA?.summary, "").replace(/\r/g, "");
    const parts1 = summary
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    const parts = parts1.length
      ? parts1
      : summary
          .split("。")
          .map((s) => s.trim())
          .filter(Boolean);

    for (const s of parts.slice(0, 3)) lines.push(`• ${s.slice(0, 120)}`);
  }

  if (lines.length === 0) {
    lines.push("• （待補充）請描述公司必須完成的制度/流程/文件/稽核等義務");
  }

  return lines.slice(0, 5).join("\n");
}

function normalizeComplianceCardForUI(id: string, input: any): IComplianceCard {
  const type: CardType = normalizeType(input?.type ?? input?.kind);
  const status: CardStatus = normalizeStatus(input?.status);
  let riskLevel: RiskLevel = normalizeRiskLevel(input?.riskLevel);

  const agencyName = safeString(input?.agencyName ?? input?.authority ?? input?.agency ?? "");
  const agencyId = safeString(input?.agencyId ?? input?.authorityId ?? "");
  const ownerDeptTop: DeptCode = safeString(input?.ownerDept ?? input?.sectionB?.ownerDept ?? "LG");

  const sectionAIn = input?.sectionA ?? {};
  const sectionBIn = input?.sectionB ?? {};

  const title = safeString(sectionAIn?.title ?? input?.title ?? input?.subject ?? "");
  const dateText = safeString(sectionAIn?.dateText ?? input?.dateText ?? "");
  const dateStr = safeString(sectionAIn?.date ?? "", "");
  const date: string | null = dateStr ? dateStr : dateText ? dateText : null;

  const summary = safeString(sectionAIn?.summary ?? input?.summary ?? "");
  const subject = safeString(sectionAIn?.subject ?? input?.subject ?? title, title);

  const sourceUrl = safeString(sectionAIn?.sourceUrl ?? input?.sourceUrl ?? input?.source ?? "");
  const announcementUrl = safeString(sectionAIn?.announcementUrl ?? input?.announcementUrl ?? input?.url ?? "");

  const attachmentLinks = Array.isArray(sectionAIn?.attachmentLinks)
    ? sectionAIn.attachmentLinks
    : splitLinks(
        sectionAIn?.attachmentLinks ??
          input?.attachmentLinks ??
          input?.attachmentLink ??
          input?.attachmentUrl ??
          ""
      );

  const attachments = Array.isArray(sectionAIn?.attachments)
    ? toAttachmentMetaArray(sectionAIn.attachments)
    : toAttachmentMetaArray(attachmentLinks);

  const sectionA: IComplianceCard["sectionA"] = {
    title,
    date,
    announcementDate: sectionAIn?.announcementDate ?? input?.announcementDate ?? null,
    dateText: dateText || (date ? String(date) : ""),
    subject,
    summary,
    sourceUrl,
    announcementUrl,
    attachmentLinks,
    legalBasis: Array.isArray(sectionAIn?.legalBasis) ? sectionAIn.legalBasis : [],
    attachments,

    // --- Regulatory Intel Meta (optional) ---
    sourceAgency: safeString(sectionAIn?.sourceAgency ?? input?.sourceAgency ?? input?.agencyName ?? agencyName, "") || undefined,
    sourceSite: safeString(sectionAIn?.sourceSite ?? input?.sourceSite ?? "", "") || undefined,
    docType: (sectionAIn?.docType ?? input?.docType) as any,
    publishDate: safeString(sectionAIn?.publishDate ?? input?.publishDate ?? dateText ?? date ?? "", "") || undefined,
    effectiveDate: safeString(sectionAIn?.effectiveDate ?? input?.effectiveDate ?? "", "") || null,
    commentDeadline: safeString(sectionAIn?.commentDeadline ?? input?.commentDeadline ?? "", "") || null,
    legalBasisRefs: Array.isArray(sectionAIn?.legalBasisRefs) ? sectionAIn.legalBasisRefs : Array.isArray(input?.legalBasisRefs) ? input.legalBasisRefs : [],
    targetEntities: Array.isArray(sectionAIn?.targetEntities) ? sectionAIn.targetEntities : Array.isArray(input?.targetEntities) ? input.targetEntities : [],
    obligationCandidates: Array.isArray(sectionAIn?.obligationCandidates) ? sectionAIn.obligationCandidates : Array.isArray(input?.obligationCandidates) ? input.obligationCandidates : [],
    evidenceSnippets: Array.isArray(sectionAIn?.evidenceSnippets) ? sectionAIn.evidenceSnippets : Array.isArray(input?.evidenceSnippets) ? input.evidenceSnippets : [],
    statusAuto: (sectionAIn?.statusAuto ?? input?.statusAuto) as any,
    statusManual: (sectionAIn?.statusManual ?? input?.statusManual) as any,
    riskLevelAuto: (sectionAIn?.riskLevelAuto ?? input?.riskLevelAuto) as any,
    riskLevelManual: (sectionAIn?.riskLevelManual ?? input?.riskLevelManual) as any,
    convertEligibility: (sectionAIn?.convertEligibility ?? input?.convertEligibility) as any,
    dedupeKey: safeString(sectionAIn?.dedupeKey ?? input?.dedupeKey ?? "", "") || undefined,
    dedupeGroupKey: safeString(sectionAIn?.dedupeGroupKey ?? input?.dedupeGroupKey ?? "", "") || undefined,

    fetchedAt: sectionAIn?.fetchedAt ?? input?.fetchedAt ?? null,
  };

  // ---- Auto inference backfill (UI-only) for legacy intel cards ----
  if (type === "intel") {
    const titleText = sectionA.title;
    const summaryText = sectionA.summary;

    // docType inference: prefer stored, then text-based, then URL hints (e.g., 行政院公報)
    let docType = safeString((sectionA as any).docType, "") || inferDocTypeLocal(titleText, summaryText);
    const urlHint = `${safeString((sectionA as any).sourceSite, "")} ${safeString(sectionA.sourceUrl, "")} ${safeString(sectionA.announcementUrl, "")}`;
    if (docType === "OTHER" || !docType) {
      if (/gazette\.nat\.gov\.tw/i.test(urlHint) || /行政院公報|公報/.test(urlHint)) docType = "ANNOUNCEMENT";
    }
    (sectionA as any).docType = docType as any;

    const pubRaw =
      safeString((sectionA as any).publishDate, "") ||
      safeString(sectionA.dateText, "") ||
      safeString(sectionA.date, "");
    const pub = normalizeDateISO(pubRaw) || pubRaw;
    (sectionA as any).publishDate = pub || undefined;

    // obligation candidates fallback (so eligibility can work even on legacy cards)
    const existingOb = Array.isArray((sectionA as any).obligationCandidates)
      ? (sectionA as any).obligationCandidates.filter(Boolean)
      : [];
    if (existingOb.length === 0) {
      const autoOb = extractObligationCandidatesLocal(titleText, summaryText);
      if (autoOb.length) (sectionA as any).obligationCandidates = autoOb;
      if (!Array.isArray((sectionA as any).evidenceSnippets) || (sectionA as any).evidenceSnippets.length === 0) {
        (sectionA as any).evidenceSnippets = extractEvidenceSnippetsLocal(titleText, summaryText, autoOb);
      }
    } else {
      if (!Array.isArray((sectionA as any).evidenceSnippets) || (sectionA as any).evidenceSnippets.length === 0) {
        (sectionA as any).evidenceSnippets = extractEvidenceSnippetsLocal(titleText, summaryText, existingOb);
      }
    }

    // statusAuto: compute regardless; override legacy S0/S2 when new inference is stronger (e.g., 公報公告通常即日生效)
    const storedStatusAuto = safeString((sectionA as any).statusAuto, "");
    const computedStatusAuto = inferStatusAutoLocal({
      docType,
      title: titleText,
      summary: summaryText,
      publishDateISO: pub,
      effectiveDateISO: (sectionA as any).effectiveDate,
    }) as RegIntelStatus;

    let statusAuto: RegIntelStatus = (storedStatusAuto as any) || computedStatusAuto;
    if (
      storedStatusAuto === "S0_UNKNOWN" ||
      (storedStatusAuto === "S2_ANNOUNCED_NOT_EFFECTIVE" && computedStatusAuto === "S3_EFFECTIVE_OBLIGATION")
    ) {
      statusAuto = computedStatusAuto;
    }
    (sectionA as any).statusAuto = statusAuto as any;

    // riskAuto: recompute when statusAuto is overridden / missing
    const storedRiskAuto = safeString((sectionA as any).riskLevelAuto, "").toUpperCase();
    const computedRiskAuto = inferRiskAutoLocal({
      statusAuto,
      docType,
      title: titleText,
      summary: summaryText,
      obligationCandidates: (sectionA as any).obligationCandidates,
    }) as RiskLevel;

    let riskAuto: RiskLevel = (storedRiskAuto as any) || computedRiskAuto;
    if (!storedRiskAuto || statusAuto === computedStatusAuto) {
      riskAuto = computedRiskAuto;
    }
    (sectionA as any).riskLevelAuto = riskAuto as any;

    const finalStatus: RegIntelStatus =
      ((safeString((sectionA as any).statusManual, "") as any) || statusAuto) as RegIntelStatus;

    // convert eligibility: compute; override when previously blocked by legacy S2 but now effective
    const storedElig = (sectionA as any).convertEligibility as any;
    const computedElig = inferConvertEligibilityLocal({
      finalStatus,
      effectiveDateISO: (sectionA as any).effectiveDate,
      obligationCandidates: (sectionA as any).obligationCandidates,
    });

    if (!storedElig || (storedElig?.ok === false && computedElig.ok === true)) {
      (sectionA as any).convertEligibility = computedElig;
    }

    const manualRisk = safeString((sectionA as any).riskLevelManual, "").toUpperCase();
    const finalRisk = (manualRisk as any) || riskAuto || riskLevel;
    riskLevel = normalizeRiskLevel(finalRisk);
  }


  const sectionB: IComplianceCard["sectionB"] = {
    ownerDept: safeString(sectionBIn?.ownerDept, ownerDeptTop),
    ownerDeptSuggestionText: safeString(
      sectionBIn?.ownerDeptSuggestionText ?? input?.ownerDeptSuggestionText,
      ""
    ),
    topicTags: Array.isArray(sectionBIn?.topicTags)
      ? sectionBIn.topicTags
      : Array.isArray(input?.topicTags)
      ? input.topicTags
      : Array.isArray(input?.tags)
      ? input.tags
      : [],
    content: safeString(sectionBIn?.content ?? input?.content, ""),
    attachments: Array.isArray(sectionBIn?.attachments) ? sectionBIn.attachments : [],
  };

  const riskScore = input?.riskScore != null ? clampInt(input?.riskScore, 50) : undefined;

  return {
    id,
    type,
    status,
    riskLevel,
    ...(riskScore != null ? { riskScore } : {}),
    agencyName: agencyName || undefined,
    agencyId: agencyId || undefined,
    ownerDept: ownerDeptTop || undefined,
    source: safeString(input?.source, ""),
    seedKey: safeString(input?.seedKey, ""),
    sectionA,
    sectionB,
    coAssistSections: Array.isArray(input?.coAssistSections) ? input.coAssistSections : [],
    approvalFlowId: safeString(input?.approvalFlowId, ""),
    approvalHistory: Array.isArray(input?.approvalHistory) ? input.approvalHistory : [],
    rejectionLog: Array.isArray(input?.rejectionLog) ? input.rejectionLog : [],
    regIntelAuditLog: Array.isArray(input?.regIntelAuditLog) ? input.regIntelAuditLog : [],
    dedupeHidden: Boolean(input?.dedupeHidden),
    duplicateOf: safeString(input?.duplicateOf, "") || undefined,
    projectTasks: Array.isArray(input?.projectTasks) ? input.projectTasks : [],
    dueDate: safeString(input?.dueDate, "") || undefined,
    createdAt: input?.createdAt ?? null,
    updatedAt: input?.updatedAt ?? null,
    archivedAt: input?.archivedAt ?? null,
    createdBy: safeString(input?.createdBy, "unknown"),
  } as IComplianceCard;
}

async function getCardById(id: string): Promise<IComplianceCard | null> {
  const snap = await getDoc(doc(db, CARDS_COLLECTION, id));
  if (!snap.exists()) return null;
  return normalizeComplianceCardForUI(id, snap.data());
}

export async function submitCardForApproval(
  cardOrId: IComplianceCard | string,
  user?: any
): Promise<void> {
  const id = typeof cardOrId === "string" ? cardOrId : safeString((cardOrId as any)?.id);
  if (!id) throw new Error("submitCardForApproval: missing card id");

  const card = typeof cardOrId === "string" ? await getCardById(id) : (cardOrId as any);
  const type: CardType = normalizeType((card as any)?.type);
  const approvalModule: ApprovalModule = type === "project" ? "GOV_PROJECT" : "OFFICIAL_REPLY";

  const uid = getUserUid(user);
  const name = getUserName(user);
  const ownerDept = safeString((card as any)?.sectionB?.ownerDept, "LG");

  // Create/reset flow first (so UI can open ApprovalFlowPanel immediately)
  await createOrResetApprovalFlow(approvalModule, id, uid, ownerDept, name);

  // Update card status + flow linkage
  await updateComplianceCard(id, {
    status: "IN_REVIEW" as any,
    approvalFlowId: `${approvalModule}_${id}` as any,
    submittedAt: new Date().toISOString(),
    submittedBy: uid,
  } as any);
}

export async function archiveCard(id: string, user?: any): Promise<void> {
  const uid = getUserUid(user);
  await updateComplianceCard(id, {
    status: "ACK_ARCHIVED" as any,
    archivedAt: new Date().toISOString(),
    archivedBy: uid,
  } as any);
}


// --- Manual review for crawler auto-determinations ---
export async function setRegIntelManualReview(
  cardOrId: IComplianceCard | string,
  params: {
    statusManual?: RegIntelStatus | "" | null;
    riskLevelManual?: RiskLevel | "" | null;
    reason: string;
    clear?: boolean;
  },
  user?: any
): Promise<void> {
  const id = typeof cardOrId === "string" ? cardOrId : safeString((cardOrId as any)?.id);
  if (!id) throw new Error("setRegIntelManualReview: missing card id");

  const reason = safeString(params?.reason, "").trim();
  if (!reason) throw new Error("請輸入覆核理由（必填）");

  const ref = doc(db, CARDS_COLLECTION, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("找不到卡片");

  const current = normalizeComplianceCardForUI(id, snap.data());

  const beforeStatus = computeFinalIntelStatus(current);
  const beforeRisk = computeFinalIntelRisk(current);

  const clear = Boolean(params?.clear);
  const nextManualStatus = clear ? "" : safeString(params?.statusManual ?? "", "").trim();
  const nextManualRisk = clear ? "" : safeString(params?.riskLevelManual ?? "", "").toUpperCase().trim();

  const afterStatus = (nextManualStatus || safeString(current?.sectionA?.statusAuto || "S0_UNKNOWN")) as RegIntelStatus;
  const afterRisk = (nextManualRisk === "HIGH" || nextManualRisk === "MEDIUM" || nextManualRisk === "LOW"
    ? (nextManualRisk as RiskLevel)
    : (safeString(current?.sectionA?.riskLevelAuto || current?.riskLevel || "MEDIUM").toUpperCase() as RiskLevel)) as RiskLevel;

  const elig = computeEligibility(afterStatus, current?.sectionA?.effectiveDate ?? null, current?.sectionA?.obligationCandidates);

  const entry = {
    at: new Date().toISOString(),
    byEmail: safeString(user?.email, "unknown"),
    byName: safeString(user?.displayName ?? user?.name ?? user?.email, "unknown"),
    before: { status: beforeStatus, risk: beforeRisk },
    after: { status: afterStatus, risk: afterRisk },
    reason,
  };

  const prevLog = Array.isArray((snap.data() as any)?.regIntelAuditLog)
    ? ((snap.data() as any).regIntelAuditLog as any[])
    : [];

  const updates: any = {
    updatedAt: serverTimestamp(),
    riskLevel: afterRisk,
    regIntelAuditLog: [...prevLog, entry],
    "sectionA.convertEligibility": elig,
  };

  if (nextManualStatus) {
    updates["sectionA.statusManual"] = afterStatus;
  } else {
    updates["sectionA.statusManual"] = (firebase as any).firestore.FieldValue.delete();
  }

  if (nextManualRisk === "HIGH" || nextManualRisk === "MEDIUM" || nextManualRisk === "LOW") {
    updates["sectionA.riskLevelManual"] = afterRisk;
  } else {
    updates["sectionA.riskLevelManual"] = (firebase as any).firestore.FieldValue.delete();
  }

  await updateDoc(ref, updates);
}

export async function moveIntelToProject(
  cardOrId: IComplianceCard | string,
  user?: any
): Promise<void> {
  const id = typeof cardOrId === "string" ? cardOrId : safeString((cardOrId as any)?.id);
  if (!id) throw new Error("moveIntelToProject: missing card id");

  const card = typeof cardOrId === "string" ? await getCardById(id) : (cardOrId as any);
  if (!card) throw new Error("找不到卡片");

  const finalStatus = computeFinalIntelStatus(card);
  const finalRisk = computeFinalIntelRisk(card);
  const elig = computeEligibility(finalStatus, card?.sectionA?.effectiveDate ?? null, card?.sectionA?.obligationCandidates);

  if (!elig.ok) {
    throw new Error(elig.reason || "目前不允許一鍵轉專案");
  }

  const uid = getUserUid(user);

  // Title
  const baseTitle = safeString(card?.sectionA?.title, "").trim() || safeString((card as any)?.title, "").trim();
  const prefix = elig.mode === "PREPARE" ? "【預備義務】" : "【法定義務】";
  const newTitle = baseTitle.startsWith("【") ? baseTitle : `${prefix}${baseTitle}`;

  // Legal basis: merge old legalBasis + extracted legalBasisRefs
  const lb = new Set<string>();
  for (const x of (Array.isArray(card?.sectionA?.legalBasis) ? card.sectionA.legalBasis : [])) lb.add(safeString(x).trim());
  for (const x of (Array.isArray(card?.sectionA?.legalBasisRefs) ? card.sectionA.legalBasisRefs : [])) lb.add(safeString(x).trim());
  const legalBasisMerged = Array.from(lb).filter(Boolean);

  // Owner dept: prefer existing ownerDept, else keep suggestion mapping
  const ownerDeptRaw = safeString(card?.sectionB?.ownerDept || (card as any)?.ownerDept || "", "").trim();
  const ownerDept = ownerDeptRaw ? mapOwnerDept(ownerDeptRaw) : mapOwnerDept(safeString(card?.sectionB?.ownerDeptSuggestionText || "LG"));

  // Build obligation summary (3-5 lines)
  const obligationSummary = buildObligationSummaryText(card);

  // Due date
  const eff = safeString(card?.sectionA?.effectiveDate || "", "").trim();
  const pub = safeString(card?.sectionA?.publishDate || card?.sectionA?.dateText || card?.sectionA?.date || "", "").trim();
  let dueDate = "";
  if (elig.mode === "PREPARE") {
    // If we know effective date, use it as target; otherwise 30 days from today.
    dueDate = eff ? eff : addDaysISO(toISODateOnly(new Date(Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), new Date().getUTCDate()))), 30);
  } else {
    const base = eff || pub || toISODateOnly(new Date(Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), new Date().getUTCDate())));
    dueDate = addDaysISO(base, 60);
  }

  // Default risk on conversion
  const newRisk: RiskLevel = elig.mode === "PREPARE" ? "MEDIUM" : (finalRisk === "LOW" ? "MEDIUM" : finalRisk);

  const now = Date.now();
  const tasks: ComplianceProjectTask[] = [
    {
      id: `t_${now}_1`,
      title: "主責部門提出『符合方案』草案（含時間表、資源需求、責任人）",
      status: "NOT_STARTED",
      ownerDept,
      dueDate: addDaysISO(dueDate, -30),
    },
    {
      id: `t_${now}_2`,
      title: "法務/法遵審閱（可選）",
      status: "NOT_STARTED",
      ownerDept: "LG",
      dueDate: addDaysISO(dueDate, -21),
    },
    {
      id: `t_${now}_3`,
      title: "資安/內控/稽核對照（可選）",
      status: "NOT_STARTED",
      ownerDept: "IT",
      dueDate: addDaysISO(dueDate, -14),
    },
    {
      id: `t_${now}_4`,
      title: "送簽核（CFO/總經理/營運長…依公司設定）",
      status: "NOT_STARTED",
      ownerDept,
      dueDate: addDaysISO(dueDate, -7),
    },
    {
      id: `t_${now}_5`,
      title: "落地執行＋證據上傳（制度文件/截圖/稽核紀錄）",
      status: "NOT_STARTED",
      ownerDept,
      dueDate,
    },
  ];

  const content = [
    "【公司應行義務摘要】",
    obligationSummary,
    "",
    "【執行計畫】",
    "（請主責單位補充：時程、資源、人員、內控/稽核對照與證據保存方式）",
  ].join("\n");

  await updateComplianceCard(id, {
    type: "project" as any,
    status: "NOT_STARTED" as any,
    obligationTemplateId: "LEGAL_OBLIGATION_PROJECT" as any,
    riskLevel: newRisk as any,
    dueDate,
    projectTasks: tasks as any,
    sectionA: {
      ...(card as any).sectionA,
      title: newTitle,
      subject: safeString((card as any).sectionA?.subject, newTitle) || newTitle,
      legalBasis: legalBasisMerged,
    } as any,
    sectionB: {
      ...(card as any).sectionB,
      ownerDept,
      content,
      attachments: Array.isArray((card as any)?.sectionB?.attachments) ? (card as any).sectionB.attachments : [],
    } as any,
    movedToProjectAt: new Date().toISOString(),
    movedToProjectBy: uid,
  } as any);
}

export async function deleteComplianceCard(id: string, _user?: any): Promise<void> {
  // Prefer server-side callable (records audit / enforces auth)
  try {
    const fn = (functions as any).httpsCallable?.("deleteComplianceCard");
    if (fn) {
      await fn({ cardId: id });
      return;
    }
  } catch (e) {
    // fallthrough
  }
  // Fallback direct delete (in case functions not deployed yet)
  await deleteDoc(doc(db, CARDS_COLLECTION, id));
}

// ----------------------------
// Crawler trigger
// ----------------------------

export async function runComplianceCrawlerNow(): Promise<{
  created: number;
  updated: number;
  dedupSkipped: number;
  runId: string;
}> {
  const fn = (functions as any).httpsCallable("runComplianceCrawlerNow");
  const res = await fn({});
  return res.data;
}

// ----------------------------
// Seed CSV -> Firestore cards
// ----------------------------

type SeedRow = {
  主管機關: string;
  發布日期: string;
  主旨: string;
  摘要: string;
  發布連結: string;
  附件下載連結: string;
  主責單位: string;
};

function coerceSeedRow(r: Record<string, string>): SeedRow {
  return {
    主管機關: r["主管機關"] ?? "",
    發布日期: r["發布日期"] ?? "",
    主旨: r["主旨"] ?? "",
    摘要: r["摘要"] ?? "",
    發布連結: r["發布連結"] ?? "",
    附件下載連結: r["附件下載連結"] ?? "",
    主責單位: r["主責單位"] ?? "",
  };
}

function splitLinks(raw: string): string[] {
  const s = safeString(raw);
  if (!s) return [];
  return s
    .split(/\s*\|\s*|\s*;\s*|\s*,\s*/g)
    .map((x) => x.trim())
    .filter(Boolean);
}

export async function refreshSeedCards(): Promise<number> {
  const resp = await fetch("/seeds/company_obligations_ads_ecom_100.csv", {
    cache: "no-store",
  });
  if (!resp.ok) {
    throw new Error(`Seed CSV 讀取失敗：${resp.status}`);
  }
  const text = await resp.text();
  const parsed = parseCsv(text).map(coerceSeedRow);

  let upserted = 0;
  for (const r of parsed) {
    // stable key
    const keyBase = `${r.主管機關}|${r.發布日期}|${r.主旨}`;
    const seedKey = await sha256Hex(keyBase);
    const docId = `seed_${seedKey.slice(0, 16)}`;

    const ref = doc(db, CARDS_COLLECTION, docId);
    const snap = await getDoc(ref);
    const now = serverTimestamp();

    const ownerDept = mapOwnerDept(r.主責單位);
    const attachmentLinks = splitLinks(r.附件下載連結);

    const payload: Record<string, any> = {
      type: "project" as CardType,
      status: "NOT_STARTED" as CardStatus,
      agencyName: safeString(r.主管機關),
      agencyId: "",
      ownerDept,
      riskScore: 50,
      riskLevel: "MEDIUM" as RiskLevel,
      source: "seed_csv:company_obligations_ads_ecom_100",
      seedKey,
      sectionA: {
        title: safeString(r.主旨),
        subject: safeString(r.主旨),
        date: safeString(r.發布日期) || null,
        dateText: safeString(r.發布日期),
        summary: safeString(r.摘要),
        sourceUrl: safeString(r.發布連結),
        announcementUrl: safeString(r.發布連結),
        attachmentLinks,
        attachments: toAttachmentMetaArray(attachmentLinks),
        legalBasis: [],
        tags: ["seed"],
      },
      sectionB: {
        ownerDept,
        ownerDeptSuggestionText: safeString(r.主責單位),
        content: "",
        attachments: [],
        topicTags: [],
      },
      coAssistSections: [],
      updatedAt: now,
    };
    if (!safeSnapExists(snap)) payload.createdAt = now;

    await setDoc(ref, payload, { merge: true });
    upserted++;
  }
  return upserted;
}

// Backward compatible alias used by existing UI
export async function seedInitialProjectCards(): Promise<{ created: number; updated: number }> {
  const count = await refreshSeedCards();
  // This seed uses stable docIds; treat all as updated for UI messaging
  return { created: 0, updated: count };
}

